import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, X } from "lucide-react";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { useSession } from "@/context/Session";
import { t } from "@/i18n";

const ROLE_LABEL = { admin: "Owner / Admin", manager: "Manager", apprentice: "Apprentice" };
const TAG_LABEL = { team: "Team", customer: "Customer", cashier: "Cashier", private: "Private" };

export function OrgCard() {
  const api = useApi();
  const { me, refresh } = useSession();
  const [name, setName] = useState(me.org.name);
  const save = async () => {
    try { await api.put("/org", { name }); toast.success(t("Saved")); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card stack-sm" data-testid="org-card">
      <p className="mono-label">{t("Organization")}</p>
      <div className="flex gap-2">
        <input className="field !py-2" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-testid="org-name-input" />
        <button className="btn btn-primary btn-sm" onClick={save} disabled={name.trim() === me.org.name} data-testid="org-name-save">{t("Save")}</button>
      </div>
      <p className="text-xs faint">{t("Customer chat")}: <code data-testid="org-customer-link">{`${window.location.origin}/c/${me.org.slug}`}</code></p>
      <LogoPicker />
    </div>
  );
}

function LogoPicker() {
  const api = useApi();
  const { me, refresh } = useSession();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await api.put("/org/logo", fd); toast.success(t("Logo updated")); refresh(); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const remove = async () => { try { await api.delete("/org/logo"); refresh(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <div className="flex items-center gap-3 pt-2" data-testid="org-logo-picker">
      {me.org.logo_url
        ? <img src={`${process.env.REACT_APP_BACKEND_URL}${me.org.logo_url}`} alt="" className="h-12 w-12 rounded-lg object-contain bg-white border border-[var(--border-color)]" data-testid="org-logo-preview" />
        : <div className="h-12 w-12 rounded-lg border border-dashed border-[var(--border-color)] grid place-items-center text-xs faint">{t("Logo")}</div>}
      <label className="btn btn-ghost btn-sm cursor-pointer">
        {t(busy ? "Uploading…" : me.org.logo_url ? "Change logo" : "Upload logo")}
        <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={upload} disabled={busy} data-testid="org-logo-input" />
      </label>
      {me.org.logo_url && <button className="text-sm faint hover:text-[var(--status-error)]" onClick={remove} data-testid="org-logo-remove">{t("Remove")}</button>}
    </div>
  );
}

export function InviteCard({ roles, onChange }) {
  const api = useApi();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState((roles.find((r) => r.base === "apprentice") || roles[roles.length - 1])?.id);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState([]);
  const load = useCallback(() => api.get("/org/invites").then((r) => setPending(r.data)).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/org/invites", { email, role });
      toast.success(t("Invite sent to {email}").replace("{email}", email));
      setEmail(""); load(); onChange?.();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const revoke = async (id) => {
    try { await api.delete(`/org/invites/${id}`); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="card stack-sm" data-testid="invite-card">
      <p className="mono-label flex items-center gap-2"><Mail size={13} /> {t("Invite people")}</p>
      <form onSubmit={send} className="flex flex-wrap gap-2">
        <input type="email" required className="field !py-2 flex-1 min-w-[12rem]" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" data-testid="invite-email-input" />
        <select className="field !py-2 !w-auto" value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role-select">
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" disabled={busy} data-testid="invite-send-button">{t(busy ? "Sending…" : "Send invite")}</button>
      </form>
      <p className="text-xs faint">{t("Each extra person adds a seat to your monthly plan once they join.")}</p>
      {pending.map((p) => (
        <div key={p.id} className="flex items-center gap-3 text-sm border-t border-[var(--border-color)] pt-2" data-testid={`invite-row-${p.id}`}>
          <span className="flex-1 min-w-0 truncate">{p.email}</span>
          <span className="chip chip-muted">{p.role_name || t(ROLE_LABEL[p.role])}</span>
          <span className="text-xs faint hidden sm:inline">{fmtDate(p.created_at)}</span>
          <button className="faint hover:text-[var(--status-error)]" onClick={() => revoke(p.id)} title={t("Cancel invite")} data-testid={`invite-revoke-${p.id}`}><X size={16} /></button>
        </div>
      ))}
    </div>
  );
}

export function RolesCard({ onChange }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [base, setBase] = useState("apprentice");
  useEffect(() => { api.get("/org/roles").then((r) => setData(r.data)).catch(() => {}); }, [api]);
  if (!data) return null;
  const save = async (roles) => {
    try { const { data: d } = await api.put("/org/roles", { roles }); setData(d); toast.success(t("Roles updated")); onChange?.(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const toggle = (role, label) => save(data.roles.map((r) => r.id !== role.id ? r : { ...r, access: r.access.includes(label) ? r.access.filter((l) => l !== label) : [...r.access, label] }));
  const add = async (e) => { e.preventDefault(); if (await save([...data.roles, { name, base, access: ["team"] }])) setName(""); };
  const del = (role) => window.confirm(t("Delete the {name} role?").replace("{name}", role.name)) && save(data.roles.filter((r) => r.id !== role.id));
  return (
    <div className="card stack-sm overflow-x-auto" data-testid="roles-card">
      <p className="mono-label">{t("Roles and who can see each label")}</p>
      <table className="w-full text-sm">
        <thead><tr className="text-left mono-label"><th className="py-2">{t("Role")}</th><th>{t("Permission")}</th>{data.labels.map((l) => <th key={l} className="text-center">{t(TAG_LABEL[l])}</th>)}<th /></tr></thead>
        <tbody>
          {data.roles.map((role) => (
            <tr key={role.id} className="border-t border-[var(--border-color)]" data-testid={`role-row-${role.id}`}>
              <td className="py-2.5 pr-2">
                <input className="field !py-1.5 font-medium min-w-[7rem]" defaultValue={role.name} maxLength={30} aria-label={t("Role name")}
                  onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== role.name) save(data.roles.map((r) => r.id === role.id ? { ...r, name: v } : r)); else e.target.value = role.name; }}
                  data-testid={`role-name-${role.id}`} />
              </td>
              <td className="pr-2">
                <select className="field !py-1.5 !w-auto" value={role.base} onChange={(e) => save(data.roles.map((r) => r.id === role.id ? { ...r, base: e.target.value } : r))} data-testid={`role-base-${role.id}`}>
                  {Object.entries(data.bases).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
                </select>
              </td>
              {data.labels.map((l) => (
                <td key={l} className="text-center">
                  <input type="checkbox" className="accent-[var(--accent-primary)] w-4 h-4" checked={role.base === "admin" || role.access.includes(l)} disabled={role.base === "admin" || l === "private"}
                    onChange={() => toggle(role, l)} data-testid={`access-${role.id}-${l}`} />
                </td>
              ))}
              <td className="text-right">{!(role.base === "admin" && data.roles.filter((r) => r.base === "admin").length === 1) && <button className="faint hover:text-[var(--status-error)] p-1" onClick={() => del(role)} title={t("Delete")} data-testid={`role-delete-${role.id}`}><X size={15} /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <form onSubmit={add} className="flex flex-wrap gap-2 pt-2">
        <input className="field !py-2 flex-1 min-w-[10rem]" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("New role, e.g. Barista")} maxLength={30} required data-testid="role-new-name" />
        <select className="field !py-2 !w-auto" value={base} onChange={(e) => setBase(e.target.value)} data-testid="role-new-base">
          {Object.entries(data.bases).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" data-testid="role-add-button">{t("Add role")}</button>
      </form>
      <p className="text-xs faint">{t("Permission sets what a role can do: owners manage everything, managers approve and review, staff ask and submit. A person can have several roles. Private procedures are never used in answers.")}</p>
    </div>
  );
}
